import { ErrorSummary, Link } from '@atoms/*';
import {
  FormTitle,
  InformationBox,
  NavigationButtons,
  RadioOptions,
} from '@molecules/*';
import { ErrorMessages } from '@types';
import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import { useBuskingStore } from '../../../../hooks/useBuskingStore';
import {
  dynamicDateErrorMessage,
  dynamicDateRangeErrorMessage,
  dynamicTimeErrorMessage,
  dynamicTimeRangeErrorMessage,
  parseZodErrors,
} from '@utils';
import {
  isIssueHappeningNowSchema,
  IssueDateAndTimeRangeSchema,
  IssueDateAndTimeSchema,
} from '@shared/schemas';
import { DateTimeFields } from '@templates';
import { useBuskingSLA } from '../../../../hooks/useBuskingSLA';
//import { formatToUTCISOString } from '@shared/utils';
import { useDateTimeUpdater } from '@shared/hooks';
import { isNumericInput } from '@shared/utils';
import { isDateTimeComplete } from '@shared/utils';

export const IssueDateTime: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, get, update } = useBuskingStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);

  const [dateErrormessage, setDateErrorMessage] = useState('');
  const [timeErrorMessage, setTimeErrorMessage] = useState('');

  const [dateRangeErrormessage, setDateRangeErrorMessage] = useState('');
  const [timeRangeErrorMessage, setTimeRangeErrorMessage] = useState('');

  const resetErrorMessages = (value: string) => {
    setDateErrorMessage('');
    setTimeErrorMessage('');
    setErrorMessages([]);
    setDateRangeErrorMessage('');
    setTimeRangeErrorMessage('');
  };

  const handleRadioInputChange = (value: string) => {
    resetErrorMessages('isIssueHappeningNow');
    update('isIssueHappeningNow', value);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;

    const isNumeric = isNumericInput(value);
    if (value && !isNumeric) return;

    const updatedDateTime = {
      ...data.selectedDateTime,
      [name]: value,
    };

    update('selectedDateTime', updatedDateTime);
    resetErrorMessages(name);
  };

  const pageTitle = 'Date and time';
  const descriptionOfTheIssue =
    'Telling us the time the issue happened helps our officers know how best to help.';

  const radioOptions = [
    {
      labelText: 'Yes',
      isDefault: get('isIssueHappeningNow') === 'Yes',
    },
    {
      labelText: 'No',
      isDefault: get('isIssueHappeningNow') === 'No',
    },
  ];

  const buttonNext = {
    onClick: () => {
      const dateInput = `${get('selectedDateTime.selectedYear')}-${get('selectedDateTime.selectedMonth')}-${get('selectedDateTime.selectedDay')}`;
      try {
        isIssueHappeningNowSchema.parse({
          isIssueHappeningNow: get('isIssueHappeningNow'),
        });
        if (get('isIssueHappeningNow').toLowerCase() === 'no') {
          IssueDateAndTimeSchema.parse(get('selectedDateTime'));
          IssueDateAndTimeRangeSchema.parse({
            yearRange: get('selectedDateTime.selectedYear'),
            monthRange: get('selectedDateTime.selectedMonth'),
            twelveMonthsRange: dateInput,
            dayRange: get('selectedDateTime.selectedDay'),
            hourRange: get('selectedDateTime.selectedHour'),
            minuteRange: get('selectedDateTime.selectedMinute'),
          });
        }
        navigate(RoutesList.PHOTO_UPLOAD);
      } catch (error: any) {
        const errors = parseZodErrors(error);
        const errorPaths = errors.map((error) => error.path);

        const newDateErrorMessage = dynamicDateErrorMessage(errorPaths);
        setDateErrorMessage(newDateErrorMessage!);

        const newTimeErrorMessage: string | undefined =
          dynamicTimeErrorMessage(errorPaths);
        setTimeErrorMessage(newTimeErrorMessage!);

        const newDateRangeErrorMessage = dynamicDateRangeErrorMessage(
          errorPaths,
          errors,
        );
        setDateRangeErrorMessage(newDateRangeErrorMessage!);

        const newTimeRangeErrorMessage: string | undefined =
          dynamicTimeRangeErrorMessage(errorPaths, errors);
        setTimeRangeErrorMessage(newTimeRangeErrorMessage!);

        setErrorMessages(errors);
      }
    },
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.ISSUE_DETAILS);
    },
  };

  const resolvedErrorMessages =
    dateErrormessage || timeErrorMessage
      ? [
          { path: 'dateErrorPath', message: dateErrormessage },
          { path: 'timeErrorMessage', message: timeErrorMessage },
        ]
      : errorMessages;

  const resolvedDateRangeErrorMessages =
    dateRangeErrormessage || timeRangeErrorMessage
      ? [
          { path: 'dateRangeErrorPath', message: dateRangeErrormessage },
          { path: 'timeRangeErrorPath', message: timeRangeErrorMessage },
        ]
      : errorMessages;

  const dateTimeCompleteBool = isDateTimeComplete(get('selectedDateTime'));

  useDateTimeUpdater({
    isIssueHappeningNow: data.isIssueHappeningNow,
    selectedDateTime: get('selectedDateTime'),
    currentDateTime: data.dateTime,
    updateDateTime: update,
  });

  const { data: slaData } = useBuskingSLA(data.dateTime);

  const infoContent = (
    <>
      <h1 className="font-semibold">Information:</h1>
      <p>
        We aim to review your report within{' '}
        {get('isIssueHappeningNow').toLowerCase() === 'yes'
          ? '1 hour.'
          : `${slaData?.value} ${slaData?.unit.toLowerCase()}`}
      </p>
      <p>
        For more information about busking and street entertainment{' '}
        <Link
          href="https://www.westminster.gov.uk/busking"
          className="text-button-default font-normal underline underline-offset-2"
          linkType="inline"
          title="Opens in a new tab"
          external={true}
        >
          Busking and street entertainment | Westminster City Council
        </Link>
      </p>
    </>
  );

  return (
    <div className="body-1 flex flex-col gap-y-2">
      <div className="mb-6">
        <FormTitle text={pageTitle} />
        <ErrorSummary
          messages={
            resolvedErrorMessages.length > 0
              ? resolvedErrorMessages
              : resolvedDateRangeErrorMessages.length > 0
                ? resolvedDateRangeErrorMessages
                : errorMessages
          }
        />
        <p className={'text-bodyLg text-text-body mb-1'}>
          {descriptionOfTheIssue}
        </p>
      </div>
      <RadioOptions
        title="Is the issue happening now?"
        onChange={(value) => handleRadioInputChange(value)}
        options={radioOptions}
        errorPath="isIssueHappeningNow"
        errorMessages={errorMessages}
      />
      {data.isIssueHappeningNow?.toLowerCase() === 'yes' && (
        <InformationBox infoText={infoContent} variant="info" title="" />
      )}
      {data.isIssueHappeningNow?.toLowerCase() === 'no' && (
        <DateTimeFields
          dateLabel="When did this issue happen?"
          dateDescription="For example, 31 01 2024"
          dateErrorPath={
            dateErrormessage?.length > 0
              ? 'dateErrorPath'
              : 'dateRangeErrorPath'
          }
          dateErrorMessages={
            dateErrormessage?.length > 0
              ? resolvedErrorMessages
              : resolvedDateRangeErrorMessages
          }
          day={get('selectedDateTime.selectedDay') as string}
          month={get('selectedDateTime.selectedMonth') as string}
          year={get('selectedDateTime.selectedYear') as string}
          onChangeDay={(e) => handleInputChange(e)}
          onChangeMonth={(e) => handleInputChange(e)}
          onChangeYear={(e) => handleInputChange(e)}
          timeLabel="What time did the issue happen?"
          timeDescription="Enter time in 24h format"
          timeErrorPath={
            timeErrorMessage?.length > 0
              ? 'timeErrorMessage'
              : 'timeRangeErrorPath'
          }
          timeErrorMessages={
            timeErrorMessage?.length > 0
              ? resolvedErrorMessages
              : resolvedDateRangeErrorMessages
          }
          hour={get('selectedDateTime.selectedHour') as string}
          minute={get('selectedDateTime.selectedMinute') as string}
          onChangeHour={(e) => handleInputChange(e)}
          onChangeMinute={(e) => handleInputChange(e)}
        />
      )}

      {data.isIssueHappeningNow?.toLowerCase() === 'no' &&
        dateTimeCompleteBool &&
        slaData?.outside_standard_hours === false && (
          <InformationBox infoText={infoContent} variant="info" title="" />
        )}
      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </div>
  );
};
